import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info, Paperclip, Send, Trash2, X } from 'lucide-react';
import {
  AcademicYearsApi,
  AnnouncementsApi,
  AnnouncementSendResult,
  AnnouncementType,
  ClassesApi,
  SectionsApi,
  UploadApi,
} from '@/services/school.api';
import { AcademicClassesApi } from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input, Select, Textarea } from '@/components/ui/Input';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/lib/cn';

const TYPE_LABEL: Record<AnnouncementType, string> = {
  class: 'Class',
  subject: 'Subject',
  noticeboard: 'Noticeboard',
};

const TYPE_TONE: Record<AnnouncementType, 'blue' | 'indigo' | 'slate'> = {
  class: 'blue',
  subject: 'indigo',
  noticeboard: 'slate',
};

const composeSchema = z.object({
  title: z.string().min(1, 'Required').max(255),
  description: z.string().min(1, 'Required').max(5000),
  academicYearId: z.string().uuid('Pick a year'),
  type: z.enum(['class', 'subject', 'noticeboard']),
  classId: z.string().optional(),
});
type ComposeForm = z.infer<typeof composeSchema>;

export function AnnouncementsPage() {
  const { can } = usePermissions();
  const canWrite = can('/announcements', 'create');
  const canDelete = can('/announcements', 'delete');

  return (
    <>
      <PageHeader title="Announcements" description="Compose class, subject, or school-wide announcements and review history." />
      {canWrite && <ComposeCard />}
      <HistoryTable canDelete={canDelete} />
    </>
  );
}

// ───── Compose ──────────────────────────────────────────────────────────────
function ComposeCard() {
  const qc = useQueryClient();
  const [selectedSectionIds, setSelectedSectionIds] = useState<Set<string>>(new Set());
  const [subjectId, setSubjectId] = useState('');
  const [files, setFiles] = useState<{ fileUrl: string; fileName: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<AnnouncementSendResult | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<ComposeForm>({
    resolver: zodResolver(composeSchema),
    defaultValues: { type: 'noticeboard' },
  });
  const type = watch('type');
  const classId = watch('classId');

  const { data: years = [] } = useQuery({ queryKey: ['academic-years'], queryFn: AcademicYearsApi.list });
  const effectiveYearId = watch('academicYearId') || years.find((y) => y.isCurrent)?.id || years[0]?.id || '';
  useEffect(() => {
    if (effectiveYearId) setValue('academicYearId', effectiveYearId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveYearId]);

  const { data: classes = [] } = useQuery({
    queryKey: ['classes', effectiveYearId],
    queryFn: () => ClassesApi.list(effectiveYearId || undefined),
    enabled: type !== 'noticeboard' && !!effectiveYearId,
  });
  const { data: sections = [] } = useQuery({
    queryKey: ['sections', classId],
    queryFn: () => SectionsApi.list(classId),
    enabled: type !== 'noticeboard' && !!classId,
  });
  const { data: subjectRows = [] } = useQuery({
    queryKey: ['class-subjects', classId],
    queryFn: () => AcademicClassesApi.subjects(classId as string),
    enabled: type === 'subject' && !!classId,
  });
  const subjects = useMemo(() => {
    const seen = new Map<string, { classSubjectId: string; subjectName: string }>();
    for (const r of subjectRows) if (!seen.has(r.subjectId)) seen.set(r.subjectId, r);
    return [...seen.values()];
  }, [subjectRows]);

  useEffect(() => {
    setSelectedSectionIds(new Set());
    setSubjectId('');
  }, [classId, type]);

  const toggleSection = (id: string) =>
    setSelectedSectionIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const uploadFile = async (file: File) => {
    setUploading(true);
    try {
      const res = await UploadApi.upload(file);
      setFiles((prev) => [...prev, { fileUrl: res.url, fileName: res.name }]);
    } finally {
      setUploading(false);
    }
  };
  const removeFile = (fileUrl: string) => setFiles((prev) => prev.filter((f) => f.fileUrl !== fileUrl));

  const create = useMutation({
    mutationFn: (f: ComposeForm) =>
      AnnouncementsApi.create({
        title: f.title,
        description: f.description,
        academicYearId: f.academicYearId,
        type: f.type,
        targets:
          f.type === 'noticeboard'
            ? undefined
            : [...selectedSectionIds].map((sectionId) => ({
                sectionId,
                classSubjectId: f.type === 'subject' ? subjectId : undefined,
              })),
        files: files.length ? files : undefined,
      }),
    onSuccess: (res) => {
      setResult(res);
      reset({ title: '', description: '', academicYearId: effectiveYearId, type: 'noticeboard' });
      setSelectedSectionIds(new Set());
      setSubjectId('');
      setFiles([]);
      qc.invalidateQueries({ queryKey: ['announcements'] });
    },
  });

  const canSubmit =
    type === 'noticeboard' ||
    (selectedSectionIds.size > 0 && (type !== 'subject' || !!subjectId));

  return (
    <div className="card mb-6 p-5">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">Compose</h2>
      <form onSubmit={handleSubmit((f) => create.mutate(f))} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Title</label>
          <Input placeholder="e.g. Sports Day" {...register('title')} />
          {errors.title && <p className="mt-1 text-xs text-red-600">{errors.title.message}</p>}
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Description</label>
          <Textarea rows={3} placeholder="Announcement body" {...register('description')} />
          {errors.description && <p className="mt-1 text-xs text-red-600">{errors.description.message}</p>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Academic year</label>
            <Select {...register('academicYearId')}>
              {years.map((y) => (
                <option key={y.id} value={y.id}>
                  {y.name}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Type</label>
            <Select {...register('type')}>
              {(Object.keys(TYPE_LABEL) as AnnouncementType[]).map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABEL[t]}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {type !== 'noticeboard' && (
          <div className="rounded-md border border-slate-200 p-3 space-y-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Class</label>
              <Select {...register('classId')}>
                <option value="">Select…</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </div>

            {type === 'subject' && classId && (
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Subject</label>
                <Select value={subjectId} onChange={(e) => setSubjectId(e.target.value)}>
                  <option value="">Select…</option>
                  {subjects.map((s) => (
                    <option key={s.classSubjectId} value={s.classSubjectId}>
                      {s.subjectName}
                    </option>
                  ))}
                </Select>
              </div>
            )}

            {classId && (
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Sections</label>
                <div className="flex flex-wrap gap-3">
                  {sections.map((sec) => (
                    <label key={sec.id} className="flex cursor-pointer items-center gap-1.5 text-sm">
                      <input type="checkbox" checked={selectedSectionIds.has(sec.id)} onChange={() => toggleSection(sec.id)} />
                      {sec.name}
                    </label>
                  ))}
                  {sections.length === 0 && <p className="text-sm text-slate-400">No sections for this class.</p>}
                </div>
                <p className="mt-2 text-xs text-slate-500">{selectedSectionIds.size} selected</p>
              </div>
            )}
          </div>
        )}

        {type === 'noticeboard' && (
          <div className="flex items-start gap-2 rounded-md bg-slate-50 p-3 text-sm text-slate-600">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            Visible to every student with a registered device — not scoped to a class or subject.
          </div>
        )}

        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Attachments</label>
          <input
            type="file"
            onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])}
            disabled={uploading}
            className="block w-full text-sm text-slate-600"
          />
          {files.length > 0 && (
            <ul className="mt-2 space-y-1">
              {files.map((f) => (
                <li key={f.fileUrl} className="flex items-center justify-between rounded-md border border-slate-200 px-2 py-1 text-sm">
                  <span className="flex items-center gap-1.5 text-slate-700">
                    <Paperclip className="h-3.5 w-3.5 text-slate-400" />
                    {f.fileName}
                  </span>
                  <button type="button" onClick={() => removeFile(f.fileUrl)} className="text-slate-400 hover:text-red-600">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {create.error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(create.error)}</div>}

        <div className="flex justify-end">
          <button type="submit" className="btn-primary" disabled={create.isPending || uploading || !canSubmit}>
            <Send className="mr-1.5 h-4 w-4" />
            {create.isPending ? 'Sending…' : 'Post announcement'}
          </button>
        </div>
      </form>

      {result && (
        <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
          <div className="text-slate-700">
            Targeted <span className="font-medium">{result.recipientCount}</span> recipient
            {result.recipientCount === 1 ? '' : 's'} — <span className="font-medium">{result.reachableCount}</span> with a
            registered device.
          </div>
          {result.send.attempted ? (
            <div className="mt-1 text-green-700">
              Delivered to {result.send.successCount ?? 0} device{result.send.successCount === 1 ? '' : 's'}
              {(result.send.failureCount ?? 0) > 0 ? `, ${result.send.failureCount} failed` : ''}.
            </div>
          ) : (
            <div className="mt-1 flex items-center gap-1.5 text-slate-500">
              <Info className="h-3.5 w-3.5" />
              Push delivery pending Firebase setup — the announcement is recorded and recipients are resolved, but no device push was sent yet.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ───── History ──────────────────────────────────────────────────────────────
function HistoryTable({ canDelete }: { canDelete: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['announcements'],
    queryFn: () => AnnouncementsApi.list({ limit: 50 }),
  });
  const items = data?.items ?? [];

  const remove = useMutation({
    mutationFn: (id: string) => AnnouncementsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements'] }),
  });

  return (
    <div>
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">History</h2>
      {isLoading ? (
        <div className="card p-8 text-center text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="card p-8 text-center text-slate-500">No announcements posted yet.</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <Th>Title</Th>
                <Th>Type</Th>
                <Th>Posted by</Th>
                <Th>Posted at</Th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((a) => (
                <tr key={a.id}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{a.title}</div>
                    <div className="max-w-md truncate text-xs text-slate-500">{a.description}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={TYPE_TONE[a.type]}>{TYPE_LABEL[a.type]}</Badge>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{a.createdByName ?? '—'}</td>
                  <td className="px-4 py-3 text-sm text-slate-500">{new Date(a.createdAt).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">
                    {canDelete && (
                      <button className="text-slate-400 hover:text-red-600" onClick={() => remove.mutate(a.id)} title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className={cn('px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500')}>{children}</th>;
}

function errMsg(e: unknown): string | undefined {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}
